import { DealerMasterKeyService, MasterKey, SelectedMasterKeyService } from '@overtur-monorepo/keying-hierarchy'
import { ConstructionKeyType, KeyNodeType, Keyway } from '@overtur-monorepo/keying-shared'
import { Component, Input, OnDestroy, OnInit } from '@angular/core'
import { Subject } from 'rxjs'
import { take, takeUntil } from 'rxjs/operators'
import { fadeIn } from '../../../animations/fadeIn'
import { Cylinder, KeySystem, Structure } from '../../../models/dev.test.models'
import { KeyingBookService } from '../../../services/keying.book.service'
import { KeyingDataSetsService } from '../../../services/keying.data.sets.service'
import { KeyingSystemService } from '../../../services/keying.system.service'
import { LoadingService } from '../../../services/loading.service'
import { ErrorService } from '../../../services/error.service'
import { KeyingCylinderService } from '../../../services/keying.cylinder.service'

@Component({
    selector: 'app-construction-key-matrix',
    templateUrl: './construction-key-matrix.component.html',
    styleUrls: ['./construction-key-matrix.component.scss'],
    animations: [fadeIn],
    standalone: false
})
export class ConstructionKeyMatrixComponent implements OnInit, OnDestroy {
  destroy$ = new Subject()
  constructionKeyType = ConstructionKeyType
  keyNodeType = KeyNodeType

  @Input() structure: Structure = null
  @Input() keySystem: KeySystem = null
  @Input() selectedCylinder: Cylinder
  @Input() selectedMasterKey: MasterKey

  cylinderKeyways: Keyway[] = []

  bittingDataSets
  matrix
  columns
  rck
  keyBitting

  defaultKeyway

  keyway
  pinningsGenerated = false

  constructor(
    public keyingDataSetsService: KeyingDataSetsService,
    public keyingBookService: KeyingBookService,
    private loadingService: LoadingService,
    private keyingSystemService: KeyingSystemService,
    private selectedMasterKeyService: SelectedMasterKeyService,
    private errorService: ErrorService,
    private dealerMasterKeyService: DealerMasterKeyService,
    private keyingCylinderService: KeyingCylinderService
  ) {}

  ngOnInit(): void {
    this.keyingBookService.bittingDataSets.pipe(takeUntil(this.destroy$)).subscribe((bittingData) => {
      if (!bittingData) {
        return
      }

      this.bittingDataSets = bittingData
      this.matrix = this.bittingDataSets.matrix
      this.columns = this.matrix.masterKeyBitting.length
      this.loadKey()
    })

    this.selectedMasterKeyService.selectedMasterKey$
      .pipe(takeUntil(this.destroy$))
      .subscribe((masterKey: MasterKey) => {
        this.selectedMasterKey = masterKey
        this.keyBitting = [...this.selectedMasterKey.bitting]
        this.loadKey()
      })
  }

  ngOnDestroy() {
    this.destroy$.next(true)
    this.destroy$.complete()
  }

  loadKey() {
    this.getKeyway()
    if (this.selectedMasterKey.masterKeyTypeId === this.keyNodeType.ConstructionKey) {
      if (
        this.keySystem.constructionTypeId === this.constructionKeyType.RCKChanges ||
        this.keySystem.constructionTypeId === this.constructionKeyType.RCKNoMaster ||
        this.keySystem.constructionTypeId === this.constructionKeyType.RCKTMK ||
        this.keySystem.constructionTypeId === this.constructionKeyType.WAFERTMK ||
        this.keySystem.constructionTypeId === this.constructionKeyType.WAFERCHANGES
      ) {
        this.getRCK()
      } else if (
        this.keySystem.constructionTypeId === this.constructionKeyType.SplitKey ||
        this.keySystem.constructionTypeId === this.constructionKeyType.BOK
      ) {
        let arrayOfPins
        let splitPosition = -1
        if (!this.keySystem.isDealerKeySystem) {
          arrayOfPins = this.keySystem.topMasterKey.split('')
          if (this.keySystem.constructionTypeId === this.constructionKeyType.BOK) {
            splitPosition = 4
          } else {
            splitPosition = 3
          }
          arrayOfPins.forEach((element, index) => {
            if (index >= splitPosition) {
              arrayOfPins[index] = 'X'
            }
          })
          this.matrix.masterKeyBitting = arrayOfPins
        } else {
          this.getDealerConstructionKey()
        }
      }
    }
  }

  getDealerConstructionKey() {
    this.dealerMasterKeyService
      .getDealerConstructionKeyBitting(this.structure.id, this.keySystem.id)
      .pipe(take(1))
      .subscribe((response) => {
        this.matrix.masterKeyBitting = response
      })
  }

  getKeyway() {
    if (this.selectedMasterKey && this.selectedMasterKey.keywayId && this.structure) {
      this.keyingDataSetsService
        .getKeyway(this.structure.keywayFamilyId, this.selectedMasterKey.keywayId)
        .pipe(take(1))
        .subscribe((keyway) => (this.keyway = keyway))
    } else {
      this.keyway = undefined
    }
  }

  calcBitting(deviceChamber) {
    if (this.structure) {
      let ballSize = -1
      if (this.structure.combinatingRuleSetId === 5) {
        ballSize = 2
      } else {
        ballSize = 3
      }

      if (!this.keySystem.isDealerKeySystem) {
        let arrayOfPins = this.keySystem.topMasterKey.split('')
        const newPinValue = Number(arrayOfPins[deviceChamber - 1]) + ballSize
        if (newPinValue > 9) {
          const message =
            'The selected device chamber is causing RCK bitting to exceed max depth value. Please adjust as needed to proceed.'
          this.errorService.showError(message)
          return
        }
        arrayOfPins[deviceChamber - 1] = newPinValue.toString()
        this.matrix.masterKeyBitting = arrayOfPins
      } else {
        this.getDealerConstructionKey()
      }
    }
  }

  getRCK() {
    this.loadingService.set({
      isLoading: true,
      message: 'Getting RCK Data',
    })
    this.keyingSystemService
      .getMasterKey(this.structure.id, this.keySystem.id, this.selectedMasterKey.id)
      .pipe(take(1))
      .subscribe((rck) => {
        this.selectedMasterKey.rck = rck
        this.calcBitting(rck.attribute.lostBallChamber)
        this.loadingService.set({
          isLoading: false,
          message: '',
        })
      })
  }
}
